import { useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ProgressRail } from "./ProgressRail";
import { WizardHeader } from "./WizardHeader";
import { WizardFooter } from "./WizardFooter";

interface WizardShellProps {
  step: number;
  direction: 1 | -1;
  children: ReactNode;
  onBack: () => void;
  onContinue: () => void;
  onMinimize: () => void;
  onClose: () => void;
  continueLabel?: string;
  continueDisabled?: boolean;
  isLoading?: boolean;
  /** Hide the footer (e.g. during extraction animation) */
  hideFooter?: boolean;
  /** Override total step count (default: 7) */
  total?: number;
  /** Override step rail labels */
  stepLabels?: string[];
  /** Override step title shown in header */
  title?: string;
  /** Override the step shown in the progress rail (logic like back/close still uses step) */
  displayStep?: number;
  /** Scan status shown persistently in the header (FE change 100). */
  scan?: { pct: number; message: string; isExtracting: boolean; isDone: boolean; celebrateLine?: string };
}

const variants = {
  enter: (dir: number) => ({ x: dir > 0 ? 36 : -36, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir > 0 ? -36 : 36, opacity: 0 }),
};

export function WizardShell({
  step,
  direction,
  children,
  onBack,
  onContinue,
  onMinimize,
  onClose,
  continueLabel,
  continueDisabled,
  isLoading,
  hideFooter,
  total = 7,
  stepLabels,
  title,
  displayStep,
  scan,
}: WizardShellProps) {
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const handleClose = () => {
    // Step 1 (own/external choice) and step 2 (URL entry) haven't captured
    // anything worth confirming away yet.
    if (step > 2) setShowCloseConfirm(true);
    else onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      <WizardHeader
        step={step}
        displayStep={displayStep}
        total={total}
        title={title}
        onMinimize={onMinimize}
        onClose={handleClose}
        showCloseConfirm={showCloseConfirm}
        onConfirmClose={onClose}
        onCancelClose={() => setShowCloseConfirm(false)}
        scan={scan}
      />
      <ProgressRail current={displayStep ?? step} total={total} labels={stepLabels} />

      <div className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.16, ease: [0.25, 0.1, 0.25, 1] }}
            className="h-full"
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </div>

      {!hideFooter && (
        <WizardFooter
          step={step}
          onBack={onBack}
          onContinue={onContinue}
          continueLabel={continueLabel}
          continueDisabled={continueDisabled}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}
